import React, { Component } from 'react';
import DateUtils from '../../../lib/DateUtils';
import JSONUtils from '../../../lib/JSONUtils';

import LoadableMonacoEditor from '../editors/monaco/LoadableMonacoEditor';
import { Grid } from '@mui/material';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import {
  RESOURCE_TYPE_SOURCE,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_DATA_SET,
} from '../../../constants/resourceTypes';
import { Accordion } from 'new/elements/Accordion';
import { Box } from 'new/elements/Box';
import Typography from 'new/elements/TypographySupport';
import { Stack } from 'new/elements/Stack';
import { ReactComponent as ErrorIcon } from 'new/components/icons/exclamation.svg';
import { Skeleton } from '@mui/material';

const styles = {
  summary: {
    text: {
      fontSize: 12,
      padding: 0,
    },
    cta: {
      padding: 0,
      marginTop: 8,
    },
  },
  changeItem: {
    row: {},
    col: {
      wordBreak: 'break-word',
    },
    colContent: {
      padding: 2,
      fontSize: 10,
    },
  },
  buttonText: {
    fontSize: 12,
    paddingLeft: 8,
    paddingRight: 8,
  },
  buttonStyle: {
    width: 'auto',
    height: 30,
    lineHeight: '30px',
  },
};

const Loader = () => {
  return (
    <Stack direction="row" gap="16px">
      <Skeleton animation="wave" variant="rounded" width="20px" height="20px" />
      <Stack className="info" gap="4px">
        <Skeleton animation="wave" variant="rounded" width="380px" />
        <Skeleton animation="wave" variant="rounded" width="300px" />
      </Stack>
      <Skeleton animation="wave" variant="rounded" width="200px" />
    </Stack>
  );
};

const getSummaryContent = (item, resourceType) => {
  const errorReason = has(item, 'error.message') ? item.error.message : '';
  let errorTitle = '';
  switch (resourceType) {
    case RESOURCE_TYPE_SOURCE:
      let sourceKey = has(item, 'nexlaMetaData.sourceKey') ? item.nexlaMetaData.sourceKey : '';
      let lineInfo =
        has(item, 'error.lineNumber') && item.error.lineNumber
          ? `line ${item.error.lineNumber} of `
          : '';
      errorTitle = `Encountered error while trying to read ${lineInfo}${sourceKey} `;
      break;

    case RESOURCE_TYPE_SINK:
      errorTitle = `Encountered error while trying to write data out`;
      break;

    case RESOURCE_TYPE_DATA_SET:
    default:
      errorTitle = `Encountered error while trying to apply transform`;
  }
  const errorTime = has(item, 'errorTime')
    ? DateUtils.convertDateStringToFormat(item.errorTime)
    : '';
  return (
    <Stack direction="row" gap="16px" width="100%">
      <Typography color="error" className="icon-container">
        <ErrorIcon fill="currentColor" width={20} height={20} />
      </Typography>
      <Stack className="info">
        <Typography variant="body2" className="title">
          {errorTitle}
        </Typography>
        <Typography variant="body2" className="reason">
          <b>Reason</b>: {errorReason}
        </Typography>
      </Stack>
      <div className="date">{errorTime}</div>
    </Stack>
  );
};

const getDetailedContent = (item, resourceType) => {
  const errorData =
    has(item, 'error') && !isNil(item.error) ? JSONUtils.stringifyJson(item.error, 2) : '';
  const rawRecord =
    has(item, 'rawMessage') && !isNil(item.rawMessage)
      ? JSONUtils.stringifyJson(item.rawMessage, 2)
      : '';

  const rawRecordTitle =
    resourceType === RESOURCE_TYPE_SOURCE
      ? 'Failed to read record'
      : resourceType === RESOURCE_TYPE_SINK
        ? 'Failed to write record'
        : 'Failed to transform record';

  return (
    <Box className="item-changes-table">
      <Grid container className="error-tab-list-item-row">
        <Grid item className="error-tab-list-item-col" lg={2} md={2} sm={2} xs={2}>
          <Typography variant="caption">Error Info</Typography>
        </Grid>
        <Grid item className="error-tab-list-item-col" lg={10} md={10} sm={10} xs={10}>
          <Stack
            overflow="hidden"
            borderRadius="10px"
            border="1px solid var(--color-outline)"
            marginLeft="16px"
          >
            <LoadableMonacoEditor
              value={errorData}
              language="json"
              height={150}
              options={{
                readOnly: true,
                minimap: { enabled: false },
              }}
            />
          </Stack>
        </Grid>
      </Grid>
      <Grid container className="error-tab-list-item-row">
        <Grid item className="error-tab-list-item-col" lg={2} md={2} sm={2} xs={2}>
          <b>{rawRecordTitle}</b>
        </Grid>
        <Grid item className="error-tab-list-item-col" lg={10} md={10} sm={10} xs={10}>
          <Stack
            overflow="hidden"
            borderRadius="10px"
            border="1px solid var(--color-outline)"
            marginLeft="16px"
          >
            <LoadableMonacoEditor
              value={rawRecord}
              language="json"
              height={150}
              options={{
                readOnly: true,
                minimap: { enabled: false },
              }}
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

class QuarantineSampleItem extends Component {
  constructor(props) {
    super(props);
  }

  render() {
    const { item, resourceType, loading } = this.props;
    const summaryText = loading ? <Loader /> : getSummaryContent(item, resourceType);
    const detailedContent = getDetailedContent(item, resourceType);
    return (
      <div className="quarantine-sample-item">
        <Accordion
          className={'error-list-item-accordion'}
          TransitionProps={{ unmountOnExit: true }}
          summary={summaryText}
        >
          {loading ? null : detailedContent}
        </Accordion>
      </div>
    );
  }
}

export default QuarantineSampleItem;
